import React, { useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import {
  fetchMyOrderDetailCard,
  fetchMyOrderTimeline,
} from "../../../../app/modules/Order/_redux/order/orderAction";
import {
  approveNameCheck,
  reRequestNameCheck,
} from "../../../../app/modules/KycList/_redux/QTasksCrud";
import SnackBarTool from "../snack-bar/SnackBar";

/**
 * How each ID name check status is shown.
 *
 * The amber is #9C4A00 rather than VAULT's usual #FF9800: on this tint that
 * one falls under 4.5:1, and these two states carry a sentence to read.
 * `pending`, `mismatch` and `rerequested` hold LockVue access and the eContract;
 * the other two are cleared.
 */
const NAME_CHECK_STYLES = {
  pending: { label: "ID name check running", color: "#9C4A00", bg: "#FFF3E0" },
  mismatch: { label: "ID name check needed", color: "#D32F2F", bg: "#FFEBEE" },
  rerequested: { label: "New ID requested", color: "#9C4A00", bg: "#FFF3E0" },
  approved: { label: "ID name confirmed", color: "#2E7D32", bg: "#E8F5E9" },
  matched: { label: "ID name matched", color: "#2E7D32", bg: "#E8F5E9" },
};

/**
 * Small pill showing the ID name check state. Renders nothing for orders the
 * check never ran on, and nothing for an automatic match unless asked, so the
 * everyday case adds no clutter.
 *
 * @param {Object} props
 * @param {string|null} props.status name_check_status from the contract
 * @param {boolean} [props.showMatched=false] Also show the "matched" state
 * @param {number} [props.fontSize=12]
 * @returns {JSX.Element|null}
 */
export function NameCheckBadge({ status, showMatched = false, fontSize = 12 }) {
  const style = NAME_CHECK_STYLES[status];
  if (!style || (status === "matched" && !showMatched)) return null;

  return (
    <span
      className="d-inline-flex align-items-center px-3 py-1 rounded-pill"
      style={{
        color: style.color,
        background: style.bg,
        border: `1px solid ${style.color}`,
        fontSize,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {style.label}
    </span>
  );
}

/**
 * What to show as the name on the ID. The backend's `id_name` holds a
 * placeholder ("(not readable)") when Stripe could not read a complete name,
 * and is empty before the check has run, so it is shown only when
 * `id_name_readable` says it is a real name.
 *
 * @param {Object} nameCheck The order's `name_check` payload
 * @returns {string}
 */
function idNameShown(nameCheck) {
  if (nameCheck.id_name_readable === true && nameCheck.id_name) return nameCheck.id_name;
  if (nameCheck.stage === "checking") return "Not read yet";
  if (nameCheck.stage === "review" || nameCheck.id_name) return "Could not be read";
  return "-";
}

/**
 * The "Name check" card in the ID Verification & Contract window: the order
 * name beside the ID name, who cleared it if anyone did, and — for a
 * reviewer on an open check — the approve and request-new-ID actions.
 *
 * Approving renames the booking to the name on the ID and issues the
 * eContract in it, unless "keep the booking name" is ticked (a business
 * booking, where the ID belongs to someone acting for the business). The
 * rename is offered only when a readable name was recorded off the ID
 * (`id_name_readable`); otherwise there is no name to rename to, so approving
 * keeps the booking name. `use_id_name` is always sent explicitly.
 *
 * @param {Object} props
 * @param {Object|null} props.nameCheck The order's `name_check` payload
 * @param {number|string} props.orderId
 * @returns {JSX.Element|null}
 */
export function NameCheckPanel({ nameCheck, orderId }) {
  const dispatch = useDispatch();
  const [note, setNote] = useState("");
  const [keepBookingName, setKeepBookingName] = useState(false);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState({ call: false, type: "", message: "", id: 0 });

  if (!nameCheck) return null;

  // Only a name actually read off the ID can be copied onto the booking. An
  // older backend without the flag reads as "no name", which keeps the name.
  const canUseIdName = nameCheck.id_name_readable === true;
  const keepName = keepBookingName || !canUseIdName;
  // `stage` separates a found mismatch from a name that could not be read;
  // `status` alone is "mismatch" for both.
  const unreadable = nameCheck.status === "mismatch" && nameCheck.stage === "review";

  /**
   * Show a notification. `id` remounts the snackbar so each message shows.
   *
   * @param {"success"|"error"} type
   * @param {string} message
   */
  const notify = (type, message) =>
    setSnack((previous) => ({ call: true, type, message, id: previous.id + 1 }));

  /**
   * Run a review action, then reload the order and its timeline.
   *
   * @param {() => Promise} request
   */
  const run = (request) => {
    setBusy(true);
    request()
      .then((res) => {
        notify("success", res?.data?.message || "Done");
        setNote("");
        setKeepBookingName(false);
        dispatch(fetchMyOrderDetailCard(orderId));
        dispatch(fetchMyOrderTimeline(orderId));
      })
      .catch((err) => {
        notify("error", err?.response?.data?.message || "Something went wrong. Please try again.");
      })
      .finally(() => setBusy(false));
  };

  /**
   * Approve with the typed note, after saying what it will do. Keeps the
   * booking name whenever there is no readable name on the ID to use, and
   * always tells the backend which it is.
   */
  const handleApprove = () => {
    if (note.trim().length < 5) {
      notify("error", "Please add a note saying how you checked the name.");
      return;
    }
    const outcome = keepName
      ? `The booking stays in the name "${nameCheck.order_name || "on the order"}", and the eContract in that name is emailed to the customer.`
      : `The booking is renamed to the name on the ID ("${nameCheck.id_name}"), and the eContract is issued in that name and emailed to the customer.`;
    if (!window.confirm(`${outcome}\n\nApprove?`)) {
      return;
    }
    run(() => approveNameCheck(orderId, note.trim(), !keepName));
  };

  /** Ask the customer for a new ID. */
  const handleReRequest = () => {
    if (!window.confirm("Email the customer a new ID verification link? Their eContract is sent once the new ID matches.")) {
      return;
    }
    run(() => reRequestNameCheck(orderId));
  };

  return (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="mb-0 fw-semibold">Name check</h6>
          <NameCheckBadge status={nameCheck.status} showMatched />
        </div>

        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <strong>Name on the order:</strong>
            <div>{nameCheck.order_name || "-"}</div>
          </div>
          <div className="col-md-6">
            <strong>Name on the ID:</strong>
            <div>{idNameShown(nameCheck)}</div>
          </div>
        </div>

        {nameCheck.status === "pending" && (
          <p className="mb-3" style={{ color: "#9C4A00" }}>
            The ID has been verified and the name is being checked. The eContract and
            LockVue access are held until it completes; this normally clears within moments.
          </p>
        )}

        {nameCheck.status === "mismatch" && !unreadable && (
          <p className="mb-3" style={{ color: "#D32F2F" }}>
            These names do not match, and the customer has been told the site manager
            will review it. The eContract has not been sent: it is issued when you
            approve below, or when the customer provides a matching ID. LockVue access
            is on hold until then.
          </p>
        )}

        {unreadable && (
          <p className="mb-3" style={{ color: "#D32F2F" }}>
            The name on the ID could not be read automatically, so it has not been
            compared with the booking. The customer has been told their ID is being
            reviewed. The eContract has not been sent: it is issued in the booking name
            when you approve below, or when the customer provides an ID whose name
            matches. LockVue access is on hold until then.
          </p>
        )}

        {nameCheck.status === "rerequested" && (
          <p className="mb-3" style={{ color: "#9C4A00" }}>
            A new ID verification link has been sent to the customer. The eContract
            and LockVue access stay on hold until their new ID matches, or you approve here.
          </p>
        )}

        {nameCheck.status === "approved" && (
          <p className="mb-3 text-muted">
            Confirmed by {nameCheck.reviewed_by || "a reviewer"}
            {nameCheck.reviewed_at && ` on ${moment(nameCheck.reviewed_at).format("DD-MM-YYYY HH:mm")}`}
            {nameCheck.note && <>: &ldquo;{nameCheck.note}&rdquo;</>}
          </p>
        )}

        {nameCheck.can_review && (
          <>
            <label htmlFor="name-check-note" className="fw-semibold mb-1">
              Note (required to approve)
            </label>
            <textarea
              id="name-check-note"
              className="form-control mb-3"
              rows={2}
              maxLength={1000}
              placeholder="e.g. Married name; marriage certificate seen at the site"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
            />
            <div className="form-check mb-3">
              <input
                id="name-check-keep-booking-name"
                type="checkbox"
                className="form-check-input"
                checked={keepName}
                onChange={(e) => setKeepBookingName(e.target.checked)}
                disabled={busy || !canUseIdName}
              />
              <label htmlFor="name-check-keep-booking-name" className="form-check-label">
                {canUseIdName
                  ? "Keep the booking name (a business booking, where the ID belongs to someone acting for the business)"
                  : "Keep the booking name (there is no readable name on the ID to use instead)"}
              </label>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={handleApprove}
                disabled={busy}
              >
                {keepName ? "Approve, keep booking name" : "Approve, use name on ID"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={handleReRequest}
                disabled={busy}
              >
                Request new ID
              </button>
            </div>
          </>
        )}
      </div>

      {snack.call && <SnackBarTool key={snack.id} type={snack.type} message={snack.message} />}
    </div>
  );
}
